import { Hexagon01 } from "@untitledui/icons";
import { CheckCircle, EditIcon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import ModalBase from "@/components/ui/modal/modal-base";
import { useCreateFence, useUpdateFence } from "../hooks/use-fences";
import { useFenceStore } from "../store/fence-store";
import type { SuccessKind } from "../types/fence";
import { mapSaveError } from "../utils/fence.utils";

export const FenceDataModal = () => {
	const {
		isDataModalOpen,
		pendingFenceData,
		isEditingFence,
		closeDataModal,
		closeFencesModal,
	} = useFenceStore();

	const createMutation = useCreateFence();
	const updateMutation = useUpdateFence();

	const [name, setName] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [successKind, setSuccessKind] = useState<SuccessKind>(null);

	const isEditing = !!isEditingFence;
	const isLoading = createMutation.isPending || updateMutation.isPending;

	useEffect(() => {
		if (isDataModalOpen) {
			setName(isEditingFence?.name || "");
			setError(null);
			setSuccessKind(null);
		}
	}, [isDataModalOpen, isEditingFence]);

	const handleCloseForm = useCallback(() => {
		setName("");
		setError(null);
		closeDataModal();
	}, [closeDataModal]);

	const handleSave = useCallback(async () => {
		if (!name.trim()) {
			setError("Por favor, insira um nome para a cerca");
			return;
		}

		if (name.length > 100) {
			setError("O nome deve ter no máximo 100 caracteres");
			return;
		}

		try {
			if (isEditing && isEditingFence) {
				await updateMutation.mutateAsync({
					id: isEditingFence.id,
					data: {
						name: name.trim(),
						type: pendingFenceData?.type ?? isEditingFence.type,
						area: pendingFenceData?.area ?? isEditingFence.area,
					},
				});
				setSuccessKind("updated");
			} else if (pendingFenceData) {
				await createMutation.mutateAsync({
					name: name.trim(),
					type: pendingFenceData.type,
					area: pendingFenceData.area,
				});
				setSuccessKind("created");
			} else {
				return;
			}

			setName("");
			setError(null);
			closeDataModal();
			closeFencesModal();
		} catch (err) {
			setError(mapSaveError(err));
		}
	}, [
		name,
		isEditing,
		isEditingFence,
		pendingFenceData,
		createMutation,
		updateMutation,
		closeDataModal,
		closeFencesModal,
	]);

	const handleCloseSuccess = useCallback(() => {
		setSuccessKind(null);
	}, []);

	if (!(isDataModalOpen || successKind)) {
		return null;
	}

	let primarySaveLabel = "Salvar";

	if (isLoading) {
		primarySaveLabel = "Salvando...";
	} else if (isEditing) {
		primarySaveLabel = "Salvar";
	}

	return (
		<>
			<ModalBase
				actions={[
					{
						label: "Cancelar",
						color: "secondary",
						onClick: handleCloseForm,
					},
					{
						label: primarySaveLabel,
						color: "primary",
						disabled: isLoading || !name.trim(),
						onClick: handleSave,
					},
				]}
				description={undefined}
				height="auto"
				icon={isEditing ? <Hexagon01 /> : <Hexagon01 />}
				iconColor={isEditing ? "gray" : "gray"}
				isOpen={isDataModalOpen}
				onClose={handleCloseForm}
				title={isEditing ? "Dados da cerca" : "Dados da cerca"}
				width={400}
			>
				<div className="mb-4">
					<label
						className="mb-1 block font-medium text-gray-700 text-sm"
						htmlFor="fence-name-input"
					>
						Nome*
					</label>
					<input
						className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
						id="fence-name-input"
						maxLength={100}
						onChange={(e) => {
							setName(e.target.value);
							setError(null);
						}}
						placeholder="Digite o nome da cerca"
						type="text"
						value={name}
					/>
					{error !== null && error !== "" ? (
						<p className="mt-1 text-red-600 text-sm">{error}</p>
					) : null}
				</div>
			</ModalBase>

			<ModalBase
				actions={[
					{
						label: "Fechar",
						color: "secondary",
						onClick: handleCloseSuccess,
					},
				]}
				description="Você pode visualizar a cerca no mapa e na lista de cercas ao efetuar uma busca."
				height="auto"
				icon={isEditing ? <EditIcon /> : <CheckCircle />}
				iconColor={isEditing ? "success" : "success"}
				isOpen={successKind !== null}
				onClose={handleCloseSuccess}
				title={
					successKind === "updated"
						? "Cerca editada com sucesso"
						: "Cerca criada com sucesso"
				}
				width={400}
			/>
		</>
	);
};
